// ADR 0023 Sprint 7 Wave B — Tenant selector band.
// Phase 3 (tenant-view switcher): added variant='global' for AppShell-level
// rendering, plus "Exit tenant view" affordance and persistent view-mode banner.
//
// Displays the currently selected tenant's name and a dropdown/combobox
// to switch among tenants returned by GET /platform/tenants.
//
// Authorization:
//   - Renders NOTHING for non-platform users.
//   - Gated on Me.is_platform_admin (ADR-0023). There is no 'platform:admin'
//     permission string on the real backend — the gate is the boolean field.
//   - The useIsPlatformAdmin check is UI-only; the backend enforces access via JWT.
//
// Selection:
//   - Writes to usePlatformTenant().setSelectedTenant.
//   - Session state only (see context.tsx security notes).
//
// Security:
//   - Tenant names are not PII — safe to render.
//   - Does NOT render subdomain or custom_domain in the selector (could
//     be used for subdomain enumeration by screen-scrapers). Just names + codes.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Building2, ChevronDown, RefreshCw, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useIsPlatformAdmin } from '@/lib/permissions'
import { listTenants } from '../api'
import { usePlatformTenant } from '../context'
import type { TenantSummary } from '../types'

interface TenantSelectorBandProps {
  /**
   * 'inline' (default) — original route-level display inside PlatformLayout
   * and AdminScenariosLayout: full selection UI always visible.
   *
   * 'global' — AppShell-level display: only visible when a tenant is selected
   * (shows a persistent "Viewing tenant: <name> — Exit" banner plus a Switch
   * button). When no tenant is selected in global mode, the component renders
   * nothing (the band only appears when it is operationally meaningful).
   */
  variant?: 'inline' | 'global'
  /**
   * When false, the "Exit tenant view" button is suppressed. Use this in
   * PlatformLayout where the AppShell-level global band (variant='global')
   * already provides Exit+Switch when a tenant is selected, preventing
   * duplicate exit/switch controls on the same screen.
   *
   * Defaults to true (Exit shown for all variants unless explicitly suppressed).
   */
  showExit?: boolean
}

export function TenantSelectorBand({ variant = 'inline', showExit = true }: TenantSelectorBandProps) {
  const isPlatformAdmin = useIsPlatformAdmin()

  // Gate: render nothing for non-platform users.
  if (!isPlatformAdmin) return null

  return <TenantSelectorBandInner variant={variant} showExit={showExit} />
}

interface TenantSelectorBandInnerProps {
  variant: 'inline' | 'global'
  showExit: boolean
}

function TenantSelectorBandInner({ variant, showExit }: TenantSelectorBandInnerProps) {
  const { selectedTenant, setSelectedTenant } = usePlatformTenant()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const tenantsQ = useQuery({
    queryKey: ['platform', 'tenants'],
    queryFn: () => listTenants(),
    staleTime: 30_000,
  })

  const tenants = tenantsQ.data?.items ?? []
  const filtered = search.trim()
    ? tenants.filter(
        (t) =>
          t.name.toLowerCase().includes(search.toLowerCase()) ||
          t.code.toLowerCase().includes(search.toLowerCase()),
      )
    : tenants

  const handleSelect = (tenant: TenantSummary) => {
    setSelectedTenant(tenant)
    setOpen(false)
    setSearch('')
  }

  const handleExit = () => {
    setSelectedTenant(null)
    setOpen(false)
    setSearch('')
  }

  // Global variant: only render when a tenant is selected.
  // When no tenant is selected, return null so the band is invisible.
  if (variant === 'global' && !selectedTenant) return null

  return (
    <div
      className={
        variant === 'global'
          ? // Global (persistent) banner: amber background to make it unmissable.
            // The operator MUST always know they are acting within a specific tenant,
            // not in platform mode. This is a compliance-critical affordance.
            'relative z-10 flex items-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-1.5 text-sm dark:border-amber-700 dark:bg-amber-950/40'
          : // Inline (route-level): neutral muted background.
            'relative flex items-center gap-2 border-b border-border bg-muted/30 px-4 py-2 text-sm'
      }
      role="status"
      aria-live={variant === 'global' ? 'polite' : undefined}
      aria-label={
        variant === 'global' && selectedTenant
          ? `Platform admin tenant-view mode: viewing ${selectedTenant.name}`
          : undefined
      }
    >
      <Building2 size={14} className="shrink-0 text-muted-fg" aria-hidden />

      {selectedTenant ? (
        <span className="flex items-center gap-2">
          {variant === 'global' && (
            <span className="font-semibold text-amber-700 dark:text-amber-300">
              Viewing as tenant:
            </span>
          )}
          <span className="font-medium">{selectedTenant.name}</span>
          <span className="rounded bg-muted px-1.5 py-px font-mono text-[0.65rem] text-muted-fg">
            {selectedTenant.code}
          </span>
          <TenantStatusDot status={selectedTenant.status} />
        </span>
      ) : (
        <span className="text-muted-fg">
          {tenantsQ.isLoading ? 'Loading tenants…' : 'No tenant selected — pick one to manage'}
        </span>
      )}

      <div className="ml-auto flex items-center gap-1">
        {/* Exit tenant view — only shown when a tenant is selected and showExit is true.
            PlatformLayout passes showExit={false} to suppress this in the inline band
            because the AppShell-level global band already shows Exit+Switch when a
            tenant is selected. AdminScenariosLayout and AdminRulesLayout use the
            default showExit={true} so they retain the Exit button. */}
        {selectedTenant && showExit && (
          <Button
            size="sm"
            variant="ghost"
            className="gap-1 text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40"
            onClick={handleExit}
            aria-label="Exit tenant view and return to platform mode"
          >
            <LogOut size={11} aria-hidden />
            Exit tenant view
          </Button>
        )}

        <Button
          size="sm"
          variant="ghost"
          className="gap-1"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Switch tenant"
        >
          {tenantsQ.isFetching ? (
            <RefreshCw size={11} className="animate-spin" aria-hidden />
          ) : (
            <ChevronDown size={11} aria-hidden />
          )}
          Switch
        </Button>
      </div>

      {open && (
        <div
          role="listbox"
          aria-label="Select tenant"
          className="absolute left-0 top-full z-50 mt-0.5 w-80 rounded border border-border bg-card shadow-lg"
        >
          {/* Search */}
          <div className="border-b border-border p-2">
            <input
              type="text"
              role="searchbox"
              aria-label="Search tenants"
              className="w-full rounded border border-border bg-card px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="Search by name or code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false)
              }}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- combobox dropdown, expected UX
              autoFocus
            />
          </div>

          {/* Options */}
          <ul className="max-h-60 overflow-y-auto py-1">
            {tenantsQ.isLoading ? (
              <li className="px-3 py-2 text-xs text-muted-fg">Loading…</li>
            ) : filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-muted-fg">
                {search ? 'No match' : 'No tenants'}
              </li>
            ) : (
              filtered.map((t) => (
                <li
                  key={t.public_id}
                  role="option"
                  aria-selected={selectedTenant?.public_id === t.public_id}
                  className="flex cursor-pointer items-center justify-between px-3 py-2 text-xs hover:bg-muted/40 aria-selected:bg-primary/10 aria-selected:text-primary"
                  onClick={() => handleSelect(t)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleSelect(t)
                  }}
                  tabIndex={0}
                >
                  <span className="flex flex-col">
                    <span className="font-medium">{t.name}</span>
                    <span className="font-mono text-[0.65rem] text-muted-fg">{t.code}</span>
                  </span>
                  <TenantStatusDot status={t.status} />
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

function TenantStatusDot({ status }: { status: TenantSummary['status'] }) {
  const cls: Record<TenantSummary['status'], string> = {
    active: 'bg-status-success',
    draft: 'bg-status-pending',
    suspended: 'bg-status-failed',
    archived: 'bg-muted',
  }
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${cls[status] ?? 'bg-muted'}`}
      title={status}
      aria-label={`Status: ${status}`}
    />
  )
}
